import { light_offer_extended } from "@/lib/models/light_offer_extended";
import Image from "next/image";
import {
  isPercentageDiscountCoupon,
  isSpecialCoupon,
  is10PercentOffCoupon,
  getOfferEndDate,
  isGachaOffer,
} from "@/lib/util";
import { readable_reward } from "@/generated/models/readable_reward";
import classNames from "classnames";
import Button from "./Button";
import { useEffect, useState } from "react";
import { useCurrentDate } from "@/lib/hooks";

interface BasketOfferProps {
  offer: light_offer_extended;
  onClick: () => void;
  onClickOfferCard: (id: number) => void;
  reward?: readable_reward;
  buttonLabel: string;
  isButtonDisabled?: boolean;
  isLoading?: boolean;
  isOutOfRange?: boolean;
}

function BasketOffer({
  offer,
  onClick,
  onClickOfferCard,
  buttonLabel,
  isLoading,
  isButtonDisabled,
  isOutOfRange = false,
}: BasketOfferProps) {
  // Debug mode for QA: show offer id when localStorage.debug === "true"
  const [showDebug, setShowDebug] = useState(false);
  const now = useCurrentDate();

  useEffect(() => {
    try {
      const value =
        typeof window !== "undefined" &&
        window.localStorage?.getItem("debug") === "true";
      queueMicrotask(() => setShowDebug(value));
    } catch {
      queueMicrotask(() => setShowDebug(false));
    }
  }, []);

  const end = getOfferEndDate(offer, now);
  const endDate = end.getDate();
  const endMonth = end.getMonth() + 1;
  const isPercentageDiscount = isPercentageDiscountCoupon(offer);

  const isOneTimeOnly = (offer.user_quota ?? 1) > 1 ? false : true;

  return (
    <div
      data-testid="basket-offer-card"
      data-offer-id={offer.id}
      data-out-of-range={isOutOfRange}
      data-disabled={Boolean(isButtonDisabled)}
      className={
        "p-0.5 gap-1 border border-none rounded-sm bg-white flex items-center justify-between cursor-pointer h-full relative"
      }
    >
      {showDebug && (
        <div
          data-testid="basket-offer-card-debug"
          className="absolute top-1 left-1 z-10 flex flex-col gap-0.5 rounded bg-black/80 text-white px-2 py-1 text-xs font-mono"
          aria-label={`Offer ID: ${offer.id}`}
        >
          <span>ID: {offer.id}</span>
          {(() => {
            if (isSpecialCoupon(offer)) return <span>Sp</span>;
            if (is10PercentOffCoupon(offer)) return <span>10%</span>;
            if (isGachaOffer(offer)) return <span>Gacha</span>;
            return null;
          })()}
        </div>
      )}

      <Image
        data-testid="basket-offer-card-image"
        onClick={() => onClickOfferCard(offer.id)}
        src={
          isOutOfRange
            ? offer.unavailable_clip_image_url || ""
            : offer.picture_url || ""
        }
        alt={offer.title}
        className={classNames("object-contain", "h-24")}
        loading="eager"
        width={95}
        height={95}
      />

      <div
        data-testid="basket-offer-card-info"
        className="flex-1 min-w-0 self-stretch flex flex-col justify-between py-1"
      >
        <div
          data-testid="basket-offer-card-title"
          className="text-base font-semibold truncate pr-6 text-ng-font-3"
          onClick={() => onClickOfferCard(offer.id)}
        >
          {offer.title}
        </div>

        {!isGachaOffer(offer) && (
          <div
            data-testid="basket-offer-card-discount-value"
            className="font-medium text-base"
          >
            {offer.discount_value}
            {isPercentageDiscount ? "%引き" : "円引き"}
          </div>
        )}

        {/* Red Banner: gacha or bl_message_ref */}
        {(isGachaOffer(offer) || offer.bl_message_ref) && (
          <div
            data-testid="basket-offer-card-restriction-banner"
            className="w-full bg-[#cb4436] text-white text-[11px] sm:text-xs text-center font-bold py-1 mt-1 mb-1"
          >
            処方せん調剤など利用不可
          </div>
        )}

        <div className={"flex items-center justify-between mt-0.5"}>
          <div className={"flex items-center gap-2"}>
            <span
              data-testid="basket-offer-card-usage-limit"
              className={
                "text-xs px-2 py-1 font-bold border-2 border-red-500 text-red-500 rounded"
              }
            >
              {isOneTimeOnly ? "1回のみ" : "何回でも"}
            </span>
            {isGachaOffer(offer) ? (
              <span
                data-testid="basket-offer-card-validity-date"
                className="offer-validity-date text-[14px] text-ng-font-1 font-medium"
              >
                {endMonth}/{endDate} {end.getHours()}:
                {String(end.getMinutes()).padStart(2, "0")}まで
              </span>
            ) : (
              <div
                data-testid="basket-offer-card-validity-date"
                className="text-[14px] text-ng-font-1 font-medium"
              >
                {endMonth}/{endDate}まで
              </div>
            )}
          </div>

          <Button
            disabled={isButtonDisabled}
            testId="basket-offer-card-action-button"
            icon={
              isLoading && (
                <div className="mr-2">
                  <div className="border-ng-main border-t-transparent rounded-full animate-spin w-3 h-3 border-2" />
                </div>
              )
            }
            onClick={() => onClick()}
            variant="outlined"
            className={"rounded-3xl text-xs max-h-5 border-2! py-3"}
            label={buttonLabel}
          ></Button>
        </div>
      </div>
    </div>
  );
}

export default BasketOffer;
